import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  Gift,
  CheckCircle,
  Clock,
  XCircle,
  Truck,
  MapPin,
  Eye,
  Download,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  MinusCircle,
  Shield
} from 'lucide-react'
import { exchangesApi } from '../../lib/api'

const ExchangeRecords = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [records, setRecords] = useState<any[]>([])
  const [allRecords, setAllRecords] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [jumpInput, setJumpInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null)
  const [showShippingModal, setShowShippingModal] = useState(false)
  const [shippingRecord, setShippingRecord] = useState<any | null>(null)
  const [trackingNo, setTrackingNo] = useState('')
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelRecord, setCancelRecord] = useState<any | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [showDeductModal, setShowDeductModal] = useState(false)
  const [deductRecord, setDeductRecord] = useState<any | null>(null)
  const [deductReason, setDeductReason] = useState('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmRecord, setConfirmRecord] = useState<any | null>(null)
  const [showReceiveModal, setShowReceiveModal] = useState(false)
  const [receiveRecord, setReceiveRecord] = useState<any | null>(null)

  // 权限判断
  const rawAuthUser = localStorage.getItem('auth_user')
  const authUser = rawAuthUser ? JSON.parse(rawAuthUser) : null
  const isSupervisor = authUser?.role === 'user' && authUser?.medicalRole === 'supervisor'
  const isAdmin = authUser?.role === 'admin'

  const filteredRecords = records.filter((record) => {
      record.userName.includes(searchTerm) ||
      (record.userPhone || '').includes(searchTerm) ||
      record.giftName.includes(searchTerm) ||
      record.hospital.includes(searchTerm)
    return true
  })

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return { label: '待兑换', icon: Clock, bg: 'bg-orange-100', textColor: 'text-orange-700' }
      case 'confirmed':
        return { label: '待发货', icon: Truck, bg: 'bg-blue-100', textColor: 'text-blue-700' }
      case 'shipping':
        return { label: '配送中', icon: Truck, bg: 'bg-purple-100', textColor: 'text-purple-700' }
      case 'completed':
        return { label: '已完成', icon: CheckCircle, bg: 'bg-green-100', textColor: 'text-green-700' }
      case 'cancelled':
        return { label: '已取消', icon: XCircle, bg: 'bg-gray-100', textColor: 'text-gray-700' }
      default:
        return { label: status, icon: Clock, bg: 'bg-gray-100', textColor: 'text-gray-700' }
    }
  }

  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successText, setSuccessText] = useState('')


  const mapRecord = (r: any) => ({
    id: r.id,
    order_no: r.order_no || '',
    userId: r.user_id,
    userName: r.user_name || '未知用户',
    userPhone: r.user_phone || '',
    giftId: r.gift_id,
    giftName: r.gift_name || '未知礼品',
    giftImage: r.gift?.image_url || '',
    points: r.points,
    status: r.status,
    createdAt: r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : '',
    deliveryType: r.delivery_type || 'express',
    address: r.address || '',
    receiverName: r.receiver_name || '',
    phone: r.phone || '',
    hospital: r.hospital_name || '',
    trackingNo: r.tracking_no || '',
    confirmedAt: r.confirmed_at ? new Date(r.confirmed_at).toLocaleString('zh-CN') : ''
  })
  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page: currentPage, page_size: pageSize }
      if (statusFilter !== 'all') params.status = statusFilter
      if (searchTerm) params.keyword = searchTerm
      const resp = await exchangesApi.list(params) as any
      const items = resp.exchanges || resp.items || resp || []
      setRecords(items.map(mapRecord))
      setTotal(resp.total || items.length)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [currentPage, pageSize, statusFilter, searchTerm])
  const loadAllRecords = useCallback(async () => {
    try {
      const resp = await exchangesApi.list({ page_size: 500 }) as any
      const items = resp.exchanges || resp.items || resp || []
      setAllRecords(items.map(mapRecord))
    } catch (e) {
      console.error(e)
    }
  }, [])
  useEffect(() => { loadRecords() }, [loadRecords])
  useEffect(() => { loadAllRecords() }, [loadAllRecords])
  useEffect(() => {
    const t = setTimeout(() => setCurrentPage(1), 400)
    return () => clearTimeout(t)
  }, [searchTerm, statusFilter])
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const statAll = allRecords.length
  const statPending = allRecords.filter(r => r.status === 'pending').length
  const statConfirmed = allRecords.filter(r => r.status === 'confirmed').length
  const statCompleted = allRecords.filter(r => r.status === 'completed').length
  const statCancelled = allRecords.filter(r => r.status === 'cancelled').length
  const updateRecord = (id: string, updates: any) => {
    const upd = (list: any[]) => list.map(r => r.id === id ? { ...r, ...updates } : r)
    setRecords(upd)
    setAllRecords(upd)
  }
  const handleConfirmExchange = async () => {
    if (!confirmRecord) return
    setActionLoading(true)
    try {
      const resp = await exchangesApi.confirm(confirmRecord.id) as any
      updateRecord(confirmRecord.id, { status: resp.status || 'completed' })
      setShowConfirmModal(false)
      setShowSuccessModal(true)
      setSuccessText('兑换已确认成功！')
    } catch (e) {
      alert('操作失败：' + (e as Error).message)
    } finally {
      setActionLoading(false)
      setConfirmRecord(null)
    }
  }
  const handleConfirmShipping = async () => {
    if (!trackingNo.trim() || !shippingRecord) {
      alert('请输入快递单号')
      return
    }
    setActionLoading(true)
    try {
      await exchangesApi.updateShipping(shippingRecord.id, trackingNo, '顺丰')
      updateRecord(shippingRecord.id, { status: 'shipping', trackingNo })
      setShowShippingModal(false)
      setShowSuccessModal(true)
      setSuccessText('发货成功！')
    } catch (e) {
      alert('操作失败：' + (e as Error).message)
    } finally {
      setActionLoading(false)
      setShippingRecord(null)
    }
  }
  const handleConfirmCancel = async () => {
    if (!cancelReason.trim() || !cancelRecord) {
      alert('请输入取消原因')
      return
    }
    setActionLoading(true)
    try {
      await exchangesApi.cancel(cancelRecord.id)
      updateRecord(cancelRecord.id, { status: 'cancelled' })
      setShowCancelModal(false)
      setShowSuccessModal(true)
      setSuccessText('兑换已取消！')
    } catch (e) {
      alert('操作失败：' + (e as Error).message)
    } finally {
      setActionLoading(false)
      setCancelRecord(null)
      setCancelReason('')
    }
  }
  const handleConfirmReceive = async () => {
    if (!receiveRecord) return
    setActionLoading(true)
    try {
      await exchangesApi.complete(receiveRecord.id)
      updateRecord(receiveRecord.id, { status: 'completed' })
      setShowReceiveModal(false)
      setShowSuccessModal(true)
      setSuccessText('签收成功！')
    } catch (e) {
      alert('操作失败：' + (e as Error).message)
    } finally {
      setActionLoading(false)
      setReceiveRecord(null)
    }
  }
  const handleConfirmDeduct = async () => {
    if (!deductReason.trim() || !deductRecord) {
      alert('请输入扣减原因')
      return
    }
    setActionLoading(true)
    try {
      const token = localStorage.getItem('token')
      const resp = await fetch(`/api/v1/exchanges/${deductRecord.id}/deduct`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: deductReason })
      })
      if (!resp.ok) throw new Error('扣减失败，请确认后端已实现该接口')
      setShowDeductModal(false)
      setShowSuccessModal(true)
      setSuccessText('积分已扣减！')
    } catch (e) {
      alert('操作失败：' + (e as Error).message)
    } finally {
      setActionLoading(false)
      setDeductRecord(null)
      setDeductReason('')
    }
  }
  // 页码数组
  const getPageNumbers = (): (number | string)[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: (number | string)[] = [1]
    if (currentPage > 3) pages.push('...')
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  const handleJump = () => {
    const p = parseInt(jumpInput)
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      setCurrentPage(p)
      setJumpInput('')
    }
  }

  const handleExport = () => {
    const headers = ['订单编号', '用户姓名', '手机号', '礼品名称', '消耗积分', '兑换时间', '兑换方式', '状态', '机构/地址']
    const rows = allRecords.map(r => {
      const orderNo = r.order_no
      const deliveryText = r.deliveryType === 'express' ? '快递配送' : `[自提] ${r.hospital}`
      return [
        orderNo,
        r.userName,
        r.userPhone || '-',
        r.giftName,
        r.points,
        r.createdAt,
        deliveryText,
        getStatusConfig(r.status).label,
        r.deliveryType === 'express' ? `${r.receiverName || ''} ${r.address || ''}`.trim() : r.hospital
      ]
    })
    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '')}"`).join(',')
    ).join('\n')
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `兑换记录_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }
  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <Gift className="w-7 h-7 mr-2 text-green-600" />
            兑换记录管理
            {isSupervisor && (
              <span className="flex items-center gap-1 text-sm font-normal text-green-600 bg-green-50 px-2 py-1 rounded-lg ml-2">
                <Shield size={14} /> 本机构
              </span>
            )}
          </h1>
          <p className="text-gray-500 text-sm mt-1">管理用户积分礼品兑换记录</p>
        </div>
        <button
          onClick={() => {
            const headers = ['订单编号', '用户姓名', '手机号', '礼品名称', '消耗积分', '兑换时间', '兑换方式', '状态', '机构/地址']
            const rows = filteredRecords.map(r => {
              const orderNo = r.order_no
              const deliveryText = r.deliveryType === 'express'
                ? '快递配送'
                : `[自提] ${r.hospital}`
              const statusText = getStatusConfig(r.status).label
              return [
                orderNo,
                r.userName,
                r.userPhone || '-',
                r.giftName,
                r.points,
                r.createdAt,
                deliveryText,
                statusText,
                r.deliveryType === 'express' ? `${r.receiverName || ''} ${r.address || ''}` : r.hospital
              ]
            })
            const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `兑换记录_${new Date().toISOString().slice(0, 10)}.csv`
            link.click()
            URL.revokeObjectURL(url)
          }}
          className="flex items-center space-x-2 bg-white border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>导出数据</span>
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button
          onClick={() => setStatusFilter('all')}
          className={`bg-white rounded-2xl shadow-md p-4 text-left hover:shadow-lg transition-shadow ${statusFilter === 'all' ? 'ring-2 ring-green-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">兑换总数</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{statAll}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`bg-white rounded-2xl shadow-md p-4 text-left hover:shadow-lg transition-shadow ${statusFilter === 'pending' ? 'ring-2 ring-orange-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">待兑换</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {statPending}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setStatusFilter('confirmed')}
          className={`bg-white rounded-2xl shadow-md p-4 text-left hover:shadow-lg transition-shadow ${statusFilter === 'confirmed' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">待发货</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {statConfirmed}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Truck className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setStatusFilter('completed')}
          className={`bg-white rounded-2xl shadow-md p-4 text-left hover:shadow-lg transition-shadow ${statusFilter === 'completed' ? 'ring-2 ring-green-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">已完成</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {statCompleted}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setStatusFilter('cancelled')}
          className={`bg-white rounded-2xl shadow-md p-4 text-left hover:shadow-lg transition-shadow ${statusFilter === 'cancelled' ? 'ring-2 ring-gray-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">已取消</p>
              <p className="text-2xl font-bold text-gray-600 mt-1">
                {statCancelled}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
              <XCircle className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </button>
      </div>

      {/* 筛选和搜索 */}
      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索用户姓名、礼品名称、医院..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: '全部' },
              { key: 'pending', label: '待兑换' },
              { key: 'confirmed', label: '待发货' },
              { key: 'shipping', label: '配送中' },
              { key: 'completed', label: '已完成' },
              { key: 'cancelled', label: '已取消' }
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setStatusFilter(filter.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  statusFilter === filter.key
                    ? 'bg-green-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 记录列表 */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">订单编号</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">用户信息</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">兑换礼品</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">兑换时间</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">兑换方式</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">状态</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {records.map((record) => {
                const statusConfig = getStatusConfig(record.status)
                const StatusIcon = statusConfig.icon
                const orderNo = record.order_no
                const isExpress = record.deliveryType === 'express'
                const [datePart, timePart] = record.createdAt.split(' ')

                return (
                  <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-mono text-gray-600">{orderNo}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-green-700 font-semibold">
                            {record.userName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{record.userName}</p>
                          <p className="text-xs text-gray-500">{record.userPhone || '-'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Gift className="w-5 h-5 text-orange-500" />
                        <span className="text-gray-800">{record.giftName}</span>
                      </div>
                      <p className="text-sm text-orange-500 font-medium mt-1">-{record.points}积分</p>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <p className="text-gray-600">{datePart}</p>
                      <p className="text-gray-500 mt-0.5">{timePart || '--:--:--'}</p>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {isExpress ? (
                        <div className="flex items-center text-blue-600">
                          <Truck className="w-4 h-4 mr-1" />
                          <span>快递配送</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-green-600">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span>[自提] {record.hospital}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.textColor}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setSelectedRecord(record)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="查看详情"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!isSupervisor && record.status === 'pending' ? (
                        <button
                          onClick={() => { setConfirmRecord(record); setShowConfirmModal(true) }}
                          className="p-2 text-green-500 hover:bg-green-50 rounded-lg transition-colors ml-1"
                          title="确认兑换"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      ) : null}
                      {!isSupervisor && record.status === 'shipping' ? (
                        <button
                          onClick={() => { setReceiveRecord(record); setShowReceiveModal(true) }}
                          className="p-2 text-purple-500 hover:bg-purple-50 rounded-lg transition-colors ml-1"
                          title="确认签收"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      ) : null}
                      {!isSupervisor && record.status === 'confirmed' && (
                        <button
                          onClick={() => { setShippingRecord(record); setTrackingNo(''); setShowShippingModal(true) }}
                          className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors ml-1"
                          title="发货"
                        >
                          <Truck className="w-4 h-4" />
                        </button>
                      )}
                      {!isSupervisor && record.status !== 'cancelled' && (
                        <button
                          onClick={() => { setCancelRecord(record); setCancelReason(''); setShowCancelModal(true) }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-1"
                          title="取消兑换"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      {!isSupervisor && record.status === 'cancelled' && (
                        <button
                          onClick={() => { setDeductRecord(record); setDeductReason(''); setShowDeductModal(true) }}
                          className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors ml-1"
                          title="重新扣减"
                        >
                          <MinusCircle className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {totalPages > 0 && (
          <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-4 border-t border-gray-100">
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span>共 {total} 条</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
                className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
              >
                <option value={10}>10 条/页</option>
                <option value={20}>20 条/页</option>
                <option value={50}>50 条/页</option>
                <option value={100}>100 条/页</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  <ChevronLeft size={16} />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
                  ) : (
                    <button key={p} onClick={() => setCurrentPage(p as number)}
                      className={`min-w-[36px] px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === p
                          ? 'bg-green-700 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}>
                      {p}
                    </button>
                  )
                )}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  <ChevronRight size={16} />
                </button>
                <div className="flex items-center gap-1 ml-2">
                  <span className="text-sm text-gray-400">跳至</span>
                  <input
                    type="text"
                    value={jumpInput}
                    onChange={(e) => setJumpInput(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                    onBlur={handleJump}
                    placeholder={String(currentPage)}
                    className="w-12 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-400">页</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">兑换详情</h3>
              <button
                onClick={() => setSelectedRecord(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* 订单编号 */}
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-sm text-gray-500">订单编号</p>
                <p className="font-medium text-gray-800 font-mono mt-1">
                  EX{selectedRecord.id.toUpperCase().padStart(8, '0')}
                </p>
              </div>

              {/* 用户信息 */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-700 text-xl font-bold">
                      {selectedRecord.userName.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{selectedRecord.userName}</p>
                    <p className="text-sm text-gray-500">{selectedRecord.userPhone || '-'}</p>
                  </div>
                </div>
              </div>

              {/* 礼品信息 */}
              <div className="bg-orange-50 rounded-xl p-4">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                    <Gift className="w-8 h-8 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{selectedRecord.giftName}</p>
                    <p className="text-orange-500 font-bold text-lg mt-1">
                      -{selectedRecord.points} 积分
                    </p>
                  </div>
                </div>
              </div>

              {/* 配送信息 */}
              {selectedRecord.deliveryType === 'express' ? (
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <Truck className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-blue-800">快递配送</span>
                  </div>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>收件人：{selectedRecord.receiverName}</p>
                    <p>联系电话：{selectedRecord.phone}</p>
                    <p>收货地址：{selectedRecord.address}</p>
                    {selectedRecord.trackingNo && <p>快递单号：{selectedRecord.trackingNo}</p>}
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <MapPin className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-medium text-green-800">医院自提</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <p>领取医院：{selectedRecord.hospital}</p>
                  </div>
                </div>
              )}

              {/* 状态和时间 */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-sm text-gray-500">兑换状态</p>
                  <p className={`font-medium mt-1 ${getStatusConfig(selectedRecord.status).textColor}`}>
                    {getStatusConfig(selectedRecord.status).label}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-sm text-gray-500">兑换时间</p>
                  <p className="font-medium text-gray-800 mt-1">{selectedRecord.createdAt}</p>
                </div>
              </div>

              {/* 操作按钮 */}
              {(selectedRecord.status === 'pending') && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => { setSelectedRecord(null); setConfirmRecord(selectedRecord); setShowConfirmModal(true) }}
                    className="flex-1 py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors"
                  >
                    确认兑换
                  </button>
                  <button
                    onClick={() => { setSelectedRecord(null); setCancelRecord(selectedRecord); setShowCancelModal(true) }}
                    className="flex-1 py-3 border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors"
                  >
                    取消兑换
                  </button>
                </div>
              )}
              {(selectedRecord.status === 'shipping') && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => { setSelectedRecord(null); setReceiveRecord(selectedRecord); setShowReceiveModal(true) }}
                    className="flex-1 py-3 bg-purple-700 text-white rounded-xl font-medium hover:bg-purple-800 transition-colors"
                  >
                    确认签收
                  </button>
                  <button
                    onClick={() => { setSelectedRecord(null); setCancelRecord(selectedRecord); setShowCancelModal(true) }}
                    className="flex-1 py-3 border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors"
                  >
                    取消兑换
                  </button>
                </div>
              )}
              {selectedRecord.status === 'confirmed' && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => { setSelectedRecord(null); setShippingRecord(selectedRecord); setShowShippingModal(true) }}
                    className="flex-1 py-3 bg-blue-700 text-white rounded-xl font-medium hover:bg-blue-800 transition-colors"
                  >
                    发货
                  </button>
                  <button
                    onClick={() => { setSelectedRecord(null); setCancelRecord(selectedRecord); setShowCancelModal(true) }}
                    className="flex-1 py-3 border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors"
                  >
                    取消兑换
                  </button>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t">
              <button
                onClick={() => setSelectedRecord(null)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 发货弹窗 */}
      {showShippingModal && shippingRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">发货</h3>
                <p className="text-blue-100 text-sm">填写快递信息</p>
              </div>
              <button
                onClick={() => setShowShippingModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">兑换礼品</p>
                <p className="font-medium text-gray-800 mt-1">{shippingRecord.giftName}</p>
                <p className="text-sm text-gray-500 mt-2">收件人：{shippingRecord.receiverName}</p>
                <p className="text-sm text-gray-500">收货地址：{shippingRecord.address}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  快递单号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="请输入快递单号"
                  value={trackingNo}
                  onChange={(e) => setTrackingNo(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowShippingModal(false)}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmShipping}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                >
                  确认发货
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 取消兑换弹窗 */}
      {showCancelModal && cancelRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">取消兑换</h3>
                <p className="text-red-100 text-sm">请填写取消原因</p>
              </div>
              <button
                onClick={() => setShowCancelModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <Gift className="w-5 h-5 text-orange-500" />
                  <span className="font-medium text-gray-800">{cancelRecord.giftName}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  兑换用户：{cancelRecord.userName} | 消耗 {cancelRecord.points} 积分
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-sm text-yellow-800">
                  <strong>取消说明：</strong>
                </p>
                <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                  <li>• 取消后积分将原路返回用户账户</li>
                  <li>• 请确认礼品尚未分发或邮寄</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  取消原因 <span className="text-red-500">*</span>
                </label>
                <textarea
                  placeholder="请输入取消原因..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmCancel}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
              >
                确认取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 确认兑换弹窗 */}
      {showConfirmModal && confirmRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">确认兑换</h3>
                <p className="text-green-100 text-sm">请核对兑换信息</p>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <Gift className="w-5 h-5 text-orange-500" />
                  <span className="font-medium text-gray-800">{confirmRecord.giftName}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  兑换用户：{confirmRecord.userName} | 消耗 {confirmRecord.points} 积分
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-800">
                  <strong>确认说明：</strong>
                </p>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>• 确认后该兑换记录将生效</li>
                  <li>• 礼品库存将相应减少</li>
                  <li>• 用户积分将扣除相应分数</li>
                </ul>
              </div>

              {confirmRecord.deliveryType === 'express' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <Truck className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-blue-800">快递配送</span>
                  </div>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>收件人：{confirmRecord.receiverName}</p>
                    <p>联系电话：{confirmRecord.phone}</p>
                    <p>收货地址：{confirmRecord.address}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <MapPin className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-medium text-green-800">医院自提</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <p>领取医院：{confirmRecord.hospital}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmExchange}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
              >
                确认兑换
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 成功提示弹窗 */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{successText}</h3>
              <p className="text-gray-500 text-sm mb-6">可在兑换记录中查看详情</p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 确认签收弹窗 */}
      {showReceiveModal && receiveRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">确认签收</h3>
                <p className="text-purple-100 text-sm">请核对签收信息</p>
              </div>
              <button
                onClick={() => setShowReceiveModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <Gift className="w-5 h-5 text-orange-500" />
                  <span className="font-medium text-gray-800">{receiveRecord.giftName}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  兑换用户：{receiveRecord.userName} | 消耗 {receiveRecord.points} 积分
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <p className="text-sm text-purple-800">
                  <strong>签收说明：</strong>
                </p>
                <ul className="text-sm text-purple-700 mt-2 space-y-1">
                  <li>• 确认后该订单状态将变为已完成</li>
                  <li>• 请确认用户已收到礼品或快递已签收</li>
                </ul>
              </div>

              {receiveRecord.deliveryType === 'express' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <Truck className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-blue-800">快递配送</span>
                  </div>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>收件人：{receiveRecord.receiverName}</p>
                    <p>联系电话：{receiveRecord.phone}</p>
                    <p>收货地址：{receiveRecord.address}</p>
                    {receiveRecord.trackingNo && <p>快递单号：{receiveRecord.trackingNo}</p>}
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <MapPin className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-medium text-green-800">医院自提</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <p>领取医院：{receiveRecord.hospital}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowReceiveModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmReceive}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 active:bg-green-800 transition-all shadow-sm"
              >
                确认签收
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 重新扣减弹窗 */}
      {showDeductModal && deductRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">重新扣减</h3>
                <p className="text-orange-100 text-sm">对已取消订单进行积分扣减</p>
              </div>
              <button
                onClick={() => setShowDeductModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <Gift className="w-5 h-5 text-orange-500" />
                  <span className="font-medium text-gray-800">{deductRecord.giftName}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  兑换用户：{deductRecord.userName} | 消耗 {deductRecord.points} 积分
                </p>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-sm text-orange-800">
                  <strong>扣减说明：</strong>
                </p>
                <ul className="text-sm text-orange-700 mt-2 space-y-1">
                  <li>• 该订单已取消，积分已返回用户账户</li>
                  <li>• 如需对礼品损坏等情况进行处罚，可重新扣减积分</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  扣减原因 <span className="text-red-500">*</span>
                </label>
                <textarea
                  placeholder="请输入扣减原因..."
                  value={deductReason}
                  onChange={(e) => setDeductReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowDeductModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmDeduct}
                className="flex-1 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors"
              >
                确认扣减
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExchangeRecords
